import axios from 'axios';

const envBase = (import.meta as any).env?.VITE_API_BASE_URL as string | undefined;
const inferredBase =
  envBase ||
  (window.location && window.location.port === '5173'
    ? 'http://127.0.0.1:8000'
    : `${window.location.protocol}//${window.location.host}`);

const API = axios.create({
  baseURL: inferredBase,
});

// Set XSRF names compatible with Django for future cookie-based auth
API.defaults.xsrfCookieName = 'csrftoken';
API.defaults.xsrfHeaderName = 'X-CSRFToken';

// Helpers to pick storage based on remember flag
const REMEMBER_KEY = 'auth_remember';
function rememberOn(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) === '1';
  } catch {
    return true;
  }
}
function readToken(): string | null {
  try {
    const ls = localStorage.getItem('token');
    const ss = sessionStorage.getItem('token');
    // Prefer localStorage if remember is on; otherwise prefer sessionStorage
    return rememberOn() ? (ls || ss) : (ss || ls);
  } catch {
    return null;
  }
}
function readRefresh(): string | null {
  try {
    const ls = localStorage.getItem('refresh');
    const ss = sessionStorage.getItem('refresh');
    return rememberOn() ? (ls || ss) : (ss || ls);
  } catch {
    return null;
  }
}
function writeToken(token: string) {
  try {
    if (rememberOn()) {
      localStorage.setItem('token', token);
      sessionStorage.removeItem('token');
    } else {
      sessionStorage.setItem('token', token);
      localStorage.removeItem('token');
    }
  } catch {}
}
function clearAllTokens() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh');
  } catch {}
}

// Attach token automatically from storage on each request, but skip auth endpoints
API.interceptors.request.use((config) => {
  const url = (config.url || '') as string;
  const isAuthEndpoint = url.includes('api/token/') || url.includes('api/password/reset');
  if (!isAuthEndpoint) {
    const token = readToken();
    if (token) {
      // merge headers safely
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      } as any;
    }
  }
  return config;
});

// Implement refresh token flow with single-flight and retry support
let isRefreshing = false as boolean;
let refreshPromise: Promise<string> | null = null;
const refreshSubscribers: Array<(token: string | null) => void> = [];

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}
function onRefreshed(token: string | null) {
  while (refreshSubscribers.length) {
    const cb = refreshSubscribers.shift();
    try {
      cb && cb(token);
    } catch {}
  }
}

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config || {};

    // Avoid attempting refresh for auth endpoints and if we've already retried
    const url: string = (originalRequest.url || '') as string;
    const isAuthEndpoint = url.includes('api/token/');
    if (status !== 401 || isAuthEndpoint || (originalRequest as any)._retry) {
      return Promise.reject(error);
    }

    const refresh = readRefresh();
    if (!refresh) {
      // No refresh token; ensure clean logout state
      clearAllTokens();
      delete API.defaults.headers.common['Authorization'];
      return Promise.reject(error);
    }

    // Queue this request until refresh completes
    (originalRequest as any)._retry = true;

    if (!isRefreshing) {
      isRefreshing = true;
      // Use a separate axios instance for refresh to avoid interceptor recursion
      const bare = axios.create({ baseURL: API.defaults.baseURL });
      refreshPromise = bare
        .post('api/token/refresh/', { refresh })
        .then(({ data }) => {
          const newAccess: string = data?.access;
          if (newAccess) {
            writeToken(newAccess);
            API.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;
            return newAccess;
          }
          return Promise.reject(new Error('No access token in refresh response'));
        })
        .catch((e) => {
          // Refresh failed; clear credentials
          clearAllTokens();
          delete API.defaults.headers.common['Authorization'];
          return Promise.reject(e);
        })
        .finally(() => {
          isRefreshing = false;
        });

      try {
        const newToken = await refreshPromise;
        onRefreshed(newToken);
      } catch (e) {
        onRefreshed(null);
      } finally {
        refreshPromise = null;
      }
    }

    // Return a promise that resolves once token refresh completes
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((newToken) => {
        if (!newToken) {
          reject(error);
          return;
        }
        // Patch the Authorization header and retry the original request
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newToken}`,
        } as any;
        resolve(API.request(originalRequest));
      });
    });
  }
);

export default API;